import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import LikeModal from './LikeModal';
import DislikeModal from './DislikeModal';

import { AppContext } from "../../AppContext";

function FeedbackPanel({
	typeOfResult,
	optPartDesc,
	mfgName,
	repPartName,
	ptPartName,
	mfgPartName,
	partDesc,
	imgURL,
	partObsolete,
	notes,
	feedbackPanelStates,
	handleFeedbackPanelStates
}) {
	const { logFeedbackHandler } = useContext(AppContext);

	const initialFeedbackState = useMemo(() => {
		return feedbackPanelStates[`${mfgPartName}${partDesc}`] || { likeClicked: false, dislikeClicked: false, feedbackText: "" };
	}, [feedbackPanelStates, mfgPartName, partDesc]);

	const [likeClicked, setLikeClicked] = useState(initialFeedbackState.likeClicked);
	const [dislikeClicked, setDislikeClicked] = useState(initialFeedbackState.dislikeClicked);
	const [feedbackText, setFeedbackText] = useState(initialFeedbackState.feedbackText || "");

	const [showLikeModal, setShowLikeModal] = useState(false);
	const [showDislikeModal, setShowDislikeModal] = useState(false);

	const [likeModalPosition, setLikeModalPosition] = useState({ bottom: 80, right: 40 });
	const [dislikeModalPosition, setDislikeModalPosition] = useState({ bottom: 80, right: 40 });

	const thumbsUpRef = useRef(null);
	const thumbsDownRef = useRef(null);

	useEffect(() => {
		setLikeClicked(initialFeedbackState.likeClicked);
		setDislikeClicked(initialFeedbackState.dislikeClicked);
		setFeedbackText(initialFeedbackState.feedbackText || "");
	}, [initialFeedbackState]);

	useEffect(() => {
		handleFeedbackPanelStates(mfgPartName, partDesc, { likeClicked, dislikeClicked, feedbackText });
	}, [likeClicked, dislikeClicked, feedbackText, handleFeedbackPanelStates, mfgPartName, partDesc]);

	const getDefaultPosition = () => {
		if (typeof window === 'undefined') {
			return { bottom: 80, right: 40 };
		}
		return {
			bottom: Math.max(window.innerHeight * 0.2, 60),
			right: Math.max(window.innerWidth * 0.1, 40),
		};
	};

	const openLikeModal = () => {
		if (thumbsUpRef.current) {
			const rect = thumbsUpRef.current.getBoundingClientRect();
			setLikeModalPosition({
				bottom: window.innerHeight - rect.bottom,
				right: window.innerWidth - rect.right,
			});
		} else {
			setLikeModalPosition(getDefaultPosition());
		}
		setShowLikeModal(true);
		setShowDislikeModal(false);
	};

	const openDislikeModal = () => {
		if (thumbsDownRef.current) {
			const buttonRect = thumbsDownRef.current.getBoundingClientRect();
			const modalWidth = 384;
			const modalHeight = 666;
			const padding = 10;

			let preferredBottom = window.innerHeight - buttonRect.bottom;
			let preferredRight = window.innerWidth - buttonRect.right;

			const modalTop = window.innerHeight - preferredBottom - modalHeight;
			const modalLeft = window.innerWidth - preferredRight - modalWidth;

			const position = {};

			if (modalTop < padding) {
				position.top = padding;
			} else {
				position.bottom = preferredBottom;
			}

			if (modalLeft < padding) {
				position.right = window.innerWidth - modalWidth - padding;
			} else {
				position.right = preferredRight;
			}

			setDislikeModalPosition(position);
		} else {
			setDislikeModalPosition(getDefaultPosition());
		}
		setShowDislikeModal(true);
		setShowLikeModal(false);
	};

	const resetModals = () => {
		setShowLikeModal(false);
		setShowDislikeModal(false);
	};

	const submitFeedback = (theyLiked, formattedFeedback, extra = {}) => {
		setLikeClicked(theyLiked);
		setDislikeClicked(!theyLiked);
		setFeedbackText(formattedFeedback);

		logFeedbackHandler(
			typeOfResult,
			optPartDesc,
			mfgName,
			repPartName,
			ptPartName,
			mfgPartName,
			partDesc,
			imgURL,
			partObsolete,
			notes,
			theyLiked,
			formattedFeedback,
			extra?.correctPart || "",
			extra?.dislikedCategory || ""
		);
		resetModals();
	};

	const handleSubmitLikeFeedback = (text) => {
		const sanitized = text && text.trim() !== "" ? text.trim() : " ";
		submitFeedback(true, sanitized);
	};

	const handleSubmitDislikeFeedback = (data) => {
		const feedbackText = data?.additionalNotes && data.additionalNotes.trim() !== ""
			? data.additionalNotes.trim()
			: " ";
		submitFeedback(false, feedbackText, {
			correctPart: data?.correctPartNumber || "",
			dislikedCategory: data?.selectedIssue || "",
		});
	};

	return (
		<div className='flex flex-row justify-evenly w-full mt-3'>
			<div className='inline-flex justify-start items-center gap-7' aria-label='feedback'>
				<button
					ref={thumbsUpRef}
					onClick={openLikeModal}
					className={`w-14 h-14 rounded-lg flex justify-center items-center transition-colors ${likeClicked ? 'bg-green-100' : 'hover:bg-gray-100'}`}
					type="button"
				>
					<img
						src="/ThumbsUp.svg"
						alt=""
						className={`w-8 h-8 transition-all ${likeClicked ? 'brightness-0 saturate-100' : ''}`}
						style={likeClicked ? { filter: 'invert(37%) sepia(93%) saturate(686%) hue-rotate(88deg) brightness(94%) contrast(86%)' } : {}}
					/>
				</button>

				<button
					ref={thumbsDownRef}
					onClick={openDislikeModal}
					className={`w-14 h-14 rounded-lg flex justify-center items-center transition-colors ${dislikeClicked ? 'bg-red-100' : 'hover:bg-gray-100'}`}
					type="button"
				>
					<img
						src="/ThumbsDown.svg"
						alt=""
						className={`w-8 h-8 transition-all ${dislikeClicked ? 'brightness-0 saturate-100' : ''}`}
						style={dislikeClicked ? { filter: 'invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)' } : {}}
					/>
				</button>
			</div>

			<LikeModal
				show={showLikeModal}
				onCancel={() => setShowLikeModal(false)}
				onSubmit={handleSubmitLikeFeedback}
				partNumber={ptPartName}
				position={likeModalPosition}
			/>

			<DislikeModal
				show={showDislikeModal}
				onCancel={() => setShowDislikeModal(false)}
				onSubmit={handleSubmitDislikeFeedback}
				partNumber={ptPartName}
				position={dislikeModalPosition}
			/>
		</div>
	);
}

export default FeedbackPanel;
